import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BookOpen, Check, FileCheck, Loader2, Search } from 'lucide-react'
import { trpc } from '@/lib/trpc'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Tabs, TabsList, TabsTrigger } from '@deepguide-ai/ui/tabs'
import { Input } from '@deepguide-ai/ui/input'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { VAULT_CATEGORY_LABELS } from '@/lib/vault-document.constants'

/**
 * A vault doc / policy the user attached to a chat message. Mirrors the
 * server's `AttachedDocumentRef`, plus a `title` for rendering the pill
 * without a second round-trip.
 */
export type AttachedDocItem = {
  documentType: 'policy' | 'vault'
  documentId: string
  title: string
}

interface AttachDocumentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Already-attached refs — shown pre-selected and counted in. */
  attached: AttachedDocItem[]
  /** Called with the full attachment set (replaces the previous set). */
  onAttach: (docs: AttachedDocItem[]) => void
}

const keyOf = (documentType: 'policy' | 'vault', documentId: string) =>
  `${documentType}:${documentId}`

/** Visual-only checkbox — the row <button> owns the click, so a nested
 * interactive control would be invalid markup. */
function CheckIndicator({ checked }: { checked: boolean }) {
  return (
    <span
      className={cn(
        'flex size-4 shrink-0 items-center justify-center rounded-sm border transition-colors',
        checked
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border-divider bg-card',
      )}
    >
      {checked && <Check className='size-3' strokeWidth={3} />}
    </span>
  )
}

export function AttachDocumentDialog({
  open,
  onOpenChange,
  attached,
  onAttach,
}: AttachDocumentDialogProps) {
  const [activeTab, setActiveTab] = useState<'policies' | 'documents'>('policies')
  const [searchQuery, setSearchQuery] = useState('')
  // Local selection (keyed) — seeded from `attached` each time the dialog opens.
  const [selected, setSelected] = useState<Map<string, AttachedDocItem>>(new Map())

  // The dialog is externally controlled (no DialogTrigger), so Radix never
  // fires onOpenChange(true) — seed/reset off the `open` prop directly.
  useEffect(() => {
    if (open) {
      setSelected(
        new Map(attached.map((a) => [keyOf(a.documentType, a.documentId), a])),
      )
    } else {
      setActiveTab('policies')
      setSearchQuery('')
    }
    // `attached` is intentionally read only at open-time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const policiesQuery = useQuery({
    ...trpc.policy.list.queryOptions({ limit: 100, offset: 0 }),
    enabled: open,
  })
  const vaultQuery = useQuery({
    ...trpc.vaultDocument.list.queryOptions({ pageSize: 100, page: 1 }),
    enabled: open,
  })

  const normalizedQuery = searchQuery.trim().toLowerCase()

  const filteredPolicies = useMemo(() => {
    const policies = policiesQuery.data?.items ?? []
    if (!normalizedQuery) return policies
    return policies.filter((p) => p.title.toLowerCase().includes(normalizedQuery))
  }, [policiesQuery.data?.items, normalizedQuery])

  const filteredVaultDocuments = useMemo(() => {
    const docs = vaultQuery.data?.items ?? []
    if (!normalizedQuery) return docs
    return docs.filter((d) => {
      const categoryLabel = (VAULT_CATEGORY_LABELS[d.category] ?? d.category) as string
      return (
        d.title.toLowerCase().includes(normalizedQuery) ||
        categoryLabel.toLowerCase().includes(normalizedQuery)
      )
    })
  }, [vaultQuery.data?.items, normalizedQuery])

  const toggle = (item: AttachedDocItem) => {
    setSelected((prev) => {
      const next = new Map(prev)
      const k = keyOf(item.documentType, item.documentId)
      if (next.has(k)) next.delete(k)
      else next.set(k, item)
      return next
    })
  }

  const isSelected = (documentType: 'policy' | 'vault', documentId: string) =>
    selected.has(keyOf(documentType, documentId))

  const handleAttach = () => {
    onAttach(Array.from(selected.values()))
    onOpenChange(false)
  }

  const isLoading =
    activeTab === 'policies' ? policiesQuery.isLoading : vaultQuery.isLoading

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[600px] overflow-hidden'>
        <DialogHeader>
          <DialogTitle>Attach a document or policy</DialogTitle>
        </DialogHeader>
        <div className='min-w-0 px-6 space-y-4'>
          <p className='text-muted-foreground text-xs'>
            Attach existing policies or documents from your vault. Vera reads
            their content to answer your message.
          </p>
          <Tabs
            value={activeTab}
            onValueChange={(v) => {
              setActiveTab(v as 'policies' | 'documents')
              setSearchQuery('')
            }}
          >
            <TabsList className='w-full'>
              <TabsTrigger value='policies' className='flex-1 text-xs'>Policies</TabsTrigger>
              <TabsTrigger value='documents' className='flex-1 text-xs'>Documents</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className='relative'>
            <Search className='text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2' />
            <Input
              type='search'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={activeTab === 'policies' ? 'Search policies…' : 'Search documents…'}
              className='h-9 pl-8 text-[12px]'
            />
          </div>
          <ScrollArea className='h-[320px]' viewClassName='[&>div]:!block'>
            <div className='space-y-1 pr-2'>
              {isLoading && (
                <div className='flex items-center justify-center py-8'>
                  <Loader2 className='text-muted-foreground h-5 w-5 animate-spin' />
                </div>
              )}

              {activeTab === 'policies' && filteredPolicies.map((policy) => {
                const checked = isSelected('policy', policy.policyDocumentId)
                return (
                  <button
                    key={policy.policyDocumentId}
                    type='button'
                    className={`flex w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors ${
                      checked ? 'border-ring bg-status-blue-bg' : 'hover:bg-muted/50'
                    }`}
                    onClick={() =>
                      toggle({
                        documentType: 'policy',
                        documentId: policy.policyDocumentId,
                        title: policy.title,
                      })
                    }
                  >
                    <CheckIndicator checked={checked} />
                    <FileCheck className='text-muted-foreground h-4 w-4 shrink-0' />
                    <div className='min-w-0 flex-1'>
                      <p className='truncate text-sm font-medium'>{policy.title}</p>
                      <p className='text-muted-foreground truncate text-xs'>
                        v{policy.currentVersion}
                        {policy.status === 'approved' && (
                          <span className='text-status-green'> · Approved</span>
                        )}
                      </p>
                    </div>
                  </button>
                )
              })}
              {activeTab === 'policies' && !isLoading && filteredPolicies.length === 0 && (
                <p className='text-muted-foreground py-8 text-center text-sm'>
                  {normalizedQuery ? `No policies match "${searchQuery}"` : 'No policies found'}
                </p>
              )}

              {activeTab === 'documents' && filteredVaultDocuments.map((doc) => {
                const checked = isSelected('vault', doc.id)
                return (
                  <button
                    key={doc.id}
                    type='button'
                    className={`flex w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors ${
                      checked ? 'border-ring bg-status-blue-bg' : 'hover:bg-muted/50'
                    }`}
                    onClick={() =>
                      toggle({ documentType: 'vault', documentId: doc.id, title: doc.title })
                    }
                  >
                    <CheckIndicator checked={checked} />
                    <BookOpen className='text-muted-foreground h-4 w-4 shrink-0' />
                    <div className='min-w-0 flex-1'>
                      <p className='truncate text-sm font-medium'>{doc.title}</p>
                      <div className='flex items-center gap-2 text-xs'>
                        <span className='text-muted-foreground truncate'>
                          {VAULT_CATEGORY_LABELS[doc.category] ?? doc.category}
                        </span>
                        {doc.fileKey && (
                          <Badge variant='outline' className='text-[10px] px-1.5 py-0 h-4 border-border-soft bg-surface-subtle text-text-subtle shrink-0'>
                            File
                          </Badge>
                        )}
                      </div>
                    </div>
                  </button>
                )
              })}
              {activeTab === 'documents' && !isLoading && filteredVaultDocuments.length === 0 && (
                <p className='text-muted-foreground py-8 text-center text-sm'>
                  {normalizedQuery ? `No documents match "${searchQuery}"` : 'No documents found'}
                </p>
              )}
            </div>
          </ScrollArea>
        </div>
        <DialogFooter className='px-6'>
          <Button variant='outline' size='sm' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size='sm' onClick={handleAttach}>
            {selected.size > 0 ? `Attach ${selected.size} selected` : 'Attach'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
